<template>
    <v-dialog v-model="isOpen" scrollable>
        <template #default>
            <v-card class="mx-auto" width="60%">
                <v-card-title><b>Studiengang</b> - {{ editStudiengang.name }}</v-card-title>

                <v-card-text>
                    <studiengang-form
                        v-model="editStudiengang"
                        :is-loading="isLoading"
                        :available-studienformen="availableStudienformen"
                        :available-abschluss-arten="availableAbschlussArten"
                        :available-standorte="availableStandorte"
                    />
                </v-card-text>

                <v-card-actions class="d-flex flex-row justify-space-between px-6">
                    <v-btn
                        class="mr-auto"
                        type="reset"
                        color="primary"
                        variant="outlined"
                        :disabled="isLoading"
                        @click="resetEditStudiengang"
                    >
                        Reset
                    </v-btn>

                    <v-btn
                        variant="tonal"
                        :loading="isLoading"
                        color="secondary"
                        @click="closeModal"
                    >
                        Abbrechen
                    </v-btn>

                    <v-btn
                        variant="tonal"
                        :loading="isLoading"
                        color="primary"
                        @click="handleSaveStudiengang"
                    >
                        Speichern
                    </v-btn>
                </v-card-actions>
            </v-card>
        </template>
    </v-dialog>
</template>

<script setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useAlertStore, useStudiengangStore } from '@/js/stores'
    import StudiengangForm from '@/js/components/Studiengang/StudiengangForm.vue'

    const props = defineProps({
        modelValue: {
            type: Boolean,
            required: true
        },
        studiengangId: {
            type: String,
            required: true
        }
    })

    const emit = defineEmits(['update:modelValue'])

    const alertStore = useAlertStore()
    const studiengangStore = useStudiengangStore()

    const isOpen = computed({
        get: () => props.modelValue,
        set: (value) => emit('update:modelValue', value)
    })

    const isLoading = computed(() => studiengangStore.isLoading)
    const availableStudienformen = computed(() => studiengangStore.availableStudienformen)
    const availableAbschlussArten = computed(() => studiengangStore.availableAbschlussArten)

    const availableStandorte = ['Köthen', 'Bernburg', 'Dessau', 'Online']

    const createEmptyEditStudiengang = () => ({
        name: '',
        kurzName: '',
        standort: '',
        fachbereich: null,
        abschlussArt: {
            abschluss: '',
            abschlussLang: ''
        },
        studienformen: {
            studienformen: []
        }
    })

    const editStudiengang = ref(createEmptyEditStudiengang())

    const resetEditStudiengang = () => {
        if (!props.studiengangId) {
            editStudiengang.value = createEmptyEditStudiengang()
            return
        }
        const studiengang = studiengangStore.getStudiengangById(props.studiengangId)
        editStudiengang.value = {
            name: studiengang.name ?? '',
            kurzName: studiengang.kurzName ?? '',
            standort: studiengang.standort ?? '',
            fachbereich: studiengang.fachbereich?.name ?? null,
            abschlussArt: {
                ...(studiengang.abschlussArt ?? { abschluss: '', abschlussLang: '' })
            },
            studienformen: {
                ...(studiengang.studienformen ?? { studienformen: [] }),
                studienformen: (studiengang.studienformen?.studienformen ?? []).map((item) => ({
                    ...item
                }))
            }
        }
    }

    const closeModal = () => {
        isOpen.value = false
    }

    const handleSaveStudiengang = async () => {
        await studiengangStore.updateStudiengang(props.studiengangId, {
            ...editStudiengang.value
        })

        if (!studiengangStore.hasError()) {
            alertStore.success('Studiengang erfolgreich gespeichert.', 'Info')
            isOpen.value = false
        } else {
            alertStore.error('Fehler beim Speichern des Studiengangs.', 'Fehler!')
        }
    }

    watch(
        () => props.studiengangId,
        () => {
            if (isOpen.value) {
                resetEditStudiengang()
            }
        }
    )

    watch(
        () => isOpen.value,
        (newValue) => {
            if (newValue) {
                resetEditStudiengang()
            }
        }
    )

    onMounted(() => {
        resetEditStudiengang()
    })
</script>

<style lang="scss" scoped></style>
